import { describeSolutionSourceState, summarizeSolutionSources } from './solution-source.js';
import { isExamCreationConfirmed, isExamCreatorLocked } from './exam-lock.js';
import type { Exam, ExamState } from '../exam.js';
import { finishedUnitCount, StepState } from '../extraction-progress.js';
import { getCollection } from '@adornis/baseql/server/collections.js';
import { Submission } from '../submission.js';
import { getPendingTaskDescs } from '../api/pending-tasks/pending-task.js';
// Side-effect imports: register pending task desc builders
import '../api/pending-tasks/set-exam-metadata.task.js';
import '../api/pending-tasks/grading-finished.task.js';
import '../api/pending-tasks/extraction-or-generation-finished.js';
import '../api/pending-tasks/online-submission-received.task.js';

interface ContextTask {
  taskNumber: string;
  taskPath: string[];
  masterSolution: boolean;
  gradingScheme: boolean;
  needsAnswer: boolean;
}

function flattenTasks(exam: Exam): ContextTask[] {
  const tasks: ContextTask[] = [];
  const sourceTasks = Array.isArray(exam?.tasks) ? (exam?.tasks as any[]) : [];

  const unwrapDoc = <T>(value: T | { doc?: T } | undefined): T | undefined => {
    if (!value) return undefined;
    if (typeof value === 'object' && value !== null && 'doc' in (value as any)) {
      return (value as any).doc as T | undefined;
    }
    return value as T;
  };

  const traverse = (node: any, ancestors: string[]) => {
    const nodeNumber = String(node?.taskNumber ?? '').trim();
    const path = [...ancestors, nodeNumber].filter(Boolean);
    const masterSolution = unwrapDoc<any>(node?.masterSolution);
    const gradingScheme = unwrapDoc<any>(node?.gradingScheme);

    tasks.push({
      taskNumber: nodeNumber,
      taskPath: path,
      masterSolution: !!masterSolution,
      gradingScheme: !!gradingScheme,
      needsAnswer: !!node?.needsAnswer,
    });

    const children = Array.isArray(node?.subTasks) ? node.subTasks : [];
    for (const child of children) traverse(child, path);
  };

  for (const main of sourceTasks) traverse(main, []);
  return tasks;
}

export async function buildExamContextString(exam: Exam, suggestions: boolean): Promise<string> {
  if (!exam.examState) return 'Exam state not initialized';

  const state: ExamState = exam.examState;

  const allTasks: string[] = [];
  const msTasks: string[] = [];
  const gsTasks: string[] = [];
  const lowConfTasks = new Set<string>();
  const errorTasks = new Set<string>();

  const totalSubmissions = state.answerExtractionProgress?.total ?? 0;
  const finishedSubmissions = finishedUnitCount(state, 'answer');

  const tasksFlat = flattenTasks(exam);

  // Provenance from the tasks themselves rather than the exam-level upload flag: an uploaded solution whose
  // gaps were generated is a mix, and the flag would report it as purely uploaded.
  const solutionSources = summarizeSolutionSources((exam as any)?.tasks);
  const msSourceText = describeSolutionSourceState(
    solutionSources.masterSolution,
    solutionSources.counts.masterSolution,
  );
  const gsSourceText = describeSolutionSourceState(solutionSources.gradingScheme, solutionSources.counts.gradingScheme);

  tasksFlat.forEach(task => {
    if (!task.needsAnswer) return; // Nur Aufgaben berücksichtigen, die eine Antwort benötigen
    allTasks.push(task.taskPath.join('-') || task.taskNumber);

    // MasterSolution tasks

    if (task.masterSolution) msTasks.push(task.taskPath.join('-') || task.taskNumber);
    // GradingScheme tasks
    if (task.gradingScheme) gsTasks.push(task.taskPath.join('-') || task.taskNumber);
  });

  const lowConfRegions = Array.isArray(state.answerExtractionProgress?.lowConfRegions)
    ? state.answerExtractionProgress.lowConfRegions
    : [];
  lowConfRegions.forEach((region: any) => {
    if (region.taskPath) lowConfTasks.add(String(region.taskPath[0] ?? ''));
  });

  const errorRegions = Array.isArray(state.answerExtractionProgress?.errorRegions)
    ? state.answerExtractionProgress.errorRegions
    : [];
  errorRegions.forEach((region: any) => {
    if (region.taskPath) errorTasks.add(String(region.taskPath[0] ?? ''));
  });

  // One state per artifact — no need to guess from the upload flag which of two states to read.
  const msStatus = state.masterSolutionProgress?.status ?? 'N/A';
  const gsStatus = state.gradingSchemeProgress?.status ?? 'N/A';

  // An uploaded file (the id array) or a run that got at least as far as starting. The former
  // `masterSolutionUploaded` flag said nothing the id array doesn't.
  const hasMasterSolution =
    (Array.isArray((exam as any)?.masterSolutionIds) && ((exam as any).masterSolutionIds as any[]).length > 0) ||
    msStatus === StepState.COMPLETED ||
    msStatus === StepState.IN_PROGRESS;
  const hasGradingScheme =
    (Array.isArray((exam as any)?.gradingSchemeIds) && ((exam as any).gradingSchemeIds as any[]).length > 0) ||
    gsStatus === StepState.COMPLETED ||
    gsStatus === StepState.IN_PROGRESS;
  const uploadCompleted = (state.uploadProgress?.uploadedFiles ?? 0) > 0;
  const taskExtractionStatus = state.taskExtractionProgress?.status;
  const answerExtractionStatus = state.answerExtractionProgress?.status;

  const submissionCollection = await getCollection(Submission);
  const submissionCount = await submissionCollection.countDocuments({ examId: exam._id });
  const hasSubmissions = submissionCount > 0;

  // The SAME predicate `create_exam`'s tool refusal, its `createExamAvailability`, the `examCreation` job's
  // own `ready()`, and the exam-editor mutations all enforce — see exam-lock.ts. Told to the agent here so
  // it never offers a structural change it would then have to refuse mid-turn.
  const examCreatorLocked = isExamCreatorLocked(exam as any);
  // Only meaningful for an exam `create_exam` made (typst source): until the creator gave its final confirm
  // (`confirm_exam_creation` / the editor's button) the PDF download and online submission are withheld.
  const hasCreatedExam = Boolean((exam as any)?.examSource);
  const examCreationConfirmed = isExamCreationConfirmed(exam as any);
  const createdExamLine = hasCreatedExam
    ? `Klausur final bestätigt (Voraussetzung für PDF-Download und Online-Abgabe; danach nicht mehr änderbar): ${examCreationConfirmed ? 'Ja' : 'Nein — Download und Online-Abgabe sind noch gesperrt'}`
    : '';

  // Bewertungsstatus
  let gradingStatus = '';
  if (hasSubmissions && allTasks.length > 0) {
    const allSubmissions = await submissionCollection.find({ examId: exam._id }).toArray();
    let totalGraded = 0;
    let totalToGrade = 0;
    allSubmissions.forEach((sub: any) => {
      if (Array.isArray(sub.taskSubmissions)) {
        sub.taskSubmissions.forEach((ts: any) => {
          totalToGrade++;
          if (ts.gradingResult?.score != null) totalGraded++;
        });
      }
    });
    gradingStatus = `* Bewertungsfortschritt: ${totalToGrade > 0 ? Math.round((totalGraded / totalToGrade) * 100) : 0}%`;
  }

  const pendingFiles = Array.isArray(exam.examState?.pendingFiles) ? exam.examState.pendingFiles : [];
  const pendingFileNames = pendingFiles
    .map((file: any) => String(file?.name || '').trim())
    .filter((name: string) => name.length > 0);
  const pendingFilesStatus = pendingFileNames.length > 0 ? pendingFileNames.join(', ') : 'Keine';

  const rawPendingTasks = Array.isArray((exam.examState as any)?.pendingTasks)
    ? (exam.examState as any).pendingTasks
    : [];
  const pendingTaskDescs = getPendingTaskDescs(rawPendingTasks);

  if (suggestions) {
    return `
EXAM CONTEXT:
- Exam ID: ${exam._id}
- Fach: ${exam.subject ?? 'N/A'}
- Thema: ${exam.topic ?? 'N/A'}
- Klasse: ${exam.class ?? 'N/A'}
- Gesamtzahl Abgaben: ${totalSubmissions}
- Verarbeitete Abgaben: ${finishedSubmissions}

WORKFLOW STATUS:
- Datei-Upload: ${uploadCompleted ? 'Abgeschlossen' : 'Ausstehend'}
- Aufgabenerkennung: ${taskExtractionStatus === StepState.COMPLETED ? 'Abgeschlossen' : taskExtractionStatus === StepState.IN_PROGRESS ? 'In Bearbeitung' : 'Ausstehend'}
- Verarbeitung der Schülerabgaben: ${answerExtractionStatus === StepState.COMPLETED ? 'Abgeschlossen' : answerExtractionStatus === StepState.IN_PROGRESS ? 'In Bearbeitung' : 'Ausstehend'}
- Musterlösung: ${msSourceText ?? (msStatus === StepState.IN_PROGRESS ? 'In Bearbeitung' : 'Noch nicht Bereit')}
- Bewertungsschema: ${gsSourceText ?? (gsStatus === StepState.IN_PROGRESS ? 'In Bearbeitung' : 'Noch nicht Bereit')}
- Bewertung der Abgaben: ${state.gradingProgress?.status === StepState.COMPLETED ? 'Abgeschlossen' : state.gradingProgress?.status === StepState.IN_PROGRESS ? 'In Bearbeitung' : 'Ausstehend'}
- Klausur-Ersteller gesperrt (keine KI-Änderungen an Struktur/Aufgaben mehr möglich): ${examCreatorLocked ? 'Ja' : 'Nein'}
${createdExamLine ? `- ${createdExamLine}\n` : ''}
Aufgabenstatus:
- Alle Aufgaben: ${allTasks.join(', ') || 'Keine'}
- Aufgaben mit Musterlösung: ${msTasks.join(', ') || 'Keine'}
- Aufgaben mit Bewertungsschema: ${gsTasks.join(', ') || 'Keine'}
- Aufgaben mit niedriger Konfidenz: ${Array.from(lowConfTasks).join(', ') || 'Keine'}
- Aufgaben mit Fehlern: ${Array.from(errorTasks).join(', ') || 'Keine'}
`.trim();
  }
  return `WICHTIGER KONTEXT ZUR KLAUSUR:
              - Exam ID: ${exam._id} | Fach: ${exam.subject} | Klasse: ${exam.class}
              - Aufgaben: ${allTasks.length} (${allTasks.join(', ')})
              - Status: Schülerabgaben (${finishedSubmissions}/${totalSubmissions} verarbeitet), Seitenzuordnung bestätigt (${exam.examState?.splitConfirmationState?.confirmed ? 'Ja' : 'Nein'}), Aufgabenstruktur bestätigt (${exam.examState?.structureConfirmationState?.confirmed ? 'Ja' : 'Nein'}), MS (${hasMasterSolution ? 'Ja' : 'Nein'}), GS (${hasGradingScheme ? 'Ja' : 'Nein'})
              - Klausur-Ersteller gesperrt (keine KI-Änderungen an Struktur/Aufgaben der Klausur mehr möglich, z.B. über das Tool "create_exam"): ${examCreatorLocked ? 'Ja' : 'Nein'}${createdExamLine ? `\n              - ${createdExamLine}` : ''}
              - Unverarbeitete Dateien: ${pendingFilesStatus}
              ${gradingStatus}${pendingTaskDescs ? `\n\n${pendingTaskDescs}` : ''}`;
}
